"use client";

import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Mail, Send, Edit2, Check, Loader2, AlertCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import { chatAPI } from "@/lib/api";
import { EmailComponent } from "./EmailComponent";

interface EmailPromptComponentProps {
  emailContent: string;
  recipientEmail: string;
  patientEmail: string;
  patientName: string;
  className?: string;
  chatId?: string;
  sessionId?: string;
}

export function EmailPromptComponent({
  emailContent,
  recipientEmail,
  patientEmail,
  patientName,
  className,
  chatId,
  sessionId,
}: EmailPromptComponentProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [sendSuccess, setSendSuccess] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);

  // Extract subject from email content
  const extractSubject = (content: string): string => {
    const lines = content.split("\n");
    const firstLine = lines[0] || "";
    if (firstLine.toLowerCase().startsWith("betreff:")) {
      return firstLine.replace(/^betreff:\s*/i, "").trim();
    }
    return firstLine.trim();
  };

  // Extract body from email content (everything after subject)
  const extractBody = (content: string): string => {
    const lines = content.split("\n");
    return lines.slice(1).join("\n").trim();
  };

  // Convert markdown to plain text for email
  const markdownToPlainText = (markdown: string): string => {
    let text = markdown;
    text = text.replace(/\*\*([^*]+)\*\*/g, (_, match) => match.toUpperCase());
    text = text.replace(/__([^_]+)__/g, (_, match) => match.toUpperCase());
    text = text.replace(/\*([^*]+)\*/g, "$1");
    text = text.replace(/_([^_]+)_/g, "$1");
    text = text.replace(/^#{1,6}\s+(.+)$/gm, (_, match) => match.toUpperCase());
    text = text.replace(/^\s*[-*+]\s+(.+)$/gm, "• $1");
    text = text.replace(/\[([^\]]+)\]\(([^)]+)\)/g, "$1 ($2)");
    text = text.replace(/```[^`]*```/g, (match) => match.replace(/```\w*\n?/g, "").trim());
    text = text.replace(/`([^`]+)`/g, "$1");
    text = text.replace(/\n{3,}/g, "\n\n");
    return text.trim();
  };

  // Create mailto link as fallback
  const createMailtoLink = () => {
    const subject = extractSubject(emailContent);
    const body = markdownToPlainText(extractBody(emailContent));
    const encodedSubject = encodeURIComponent(subject);
    const encodedBody = encodeURIComponent(body);
    const ccEmail = "nils.zaenker@charite.de";
    return `mailto:${recipientEmail}?subject=${encodedSubject}&body=${encodedBody}&cc=${ccEmail}`;
  };

  const handleDirectSend = async () => {
    setIsSending(true);
    setSendError(null);

    try {
      const subject = extractSubject(emailContent);
      const body = markdownToPlainText(extractBody(emailContent));

      const result = await chatAPI.sendEmail({
        recipientEmail,
        patientEmail,
        patientName,
        subject,
        body,
        chatId,
      });

      if (result.success) {
        setSendSuccess(true);
      } else {
        // Silently fall back to mailto on failure
        console.warn("Email API failed, falling back to mailto:", result.error);
        window.open(createMailtoLink(), "_self");
      }
    } catch (error) {
      // Silently fall back to mailto on error
      console.warn("Email send error, falling back to mailto:", error);
      window.open(createMailtoLink(), "_self");
    } finally {
      setIsSending(false);
    }
  };

  const subject = extractSubject(emailContent);

  // Show success state
  if (sendSuccess) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className={cn(
          "rounded-xl bg-green-50 border border-green-200 p-4",
          className
        )}
      >
        <div className="flex items-center gap-3">
          <div className="shrink-0 w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
            <Check className="h-5 w-5 text-green-600" />
          </div>
          <div>
            <p className="text-sm font-medium text-green-800">
              E-Mail erfolgreich gesendet!
            </p>
            <p className="text-xs text-green-600 mt-0.5">
              Die Anfrage wurde an {recipientEmail} gesendet.
            </p>
          </div>
        </div>
      </motion.div>
    );
  }

  // Show expanded EmailComponent for editing
  if (isExpanded) {
    return (
      <EmailComponent
        emailContent={emailContent}
        recipientEmail={recipientEmail}
        patientEmail={patientEmail}
        patientName={patientName}
        className={className}
        chatId={chatId}
        sessionId={sessionId}
        onSendSuccess={() => setSendSuccess(true)}
      />
    );
  }

  // Show compact prompt
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, ease: "easeOut" }}
      className={cn(
        "rounded-xl bg-(--outer-bg) border border-border shadow-xs hover:shadow-md transition-shadow",
        className
      )}
    >
      {/* Header with email preview */}
      <div className="p-4">
        <div className="flex items-start gap-3">
          <div className="shrink-0 w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
            <Mail className="h-5 w-5 text-primary-600" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-foreground">
              E-Mail an Studienzentrum
            </p>
            <p className="text-xs text-muted-foreground mt-0.5 truncate">
              An: {recipientEmail}
            </p>
            <p className="text-xs text-muted-foreground truncate">
              Betreff: {subject}
            </p>
          </div>
        </div>
      </div>

      {/* Error message if any */}
      <AnimatePresence>
        {sendError && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="px-4 pb-2"
          >
            <div className="flex items-center gap-2 text-xs text-red-600 bg-red-50 rounded-lg p-2">
              <AlertCircle className="h-3.5 w-3.5 shrink-0" />
              <span>{sendError}</span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Action buttons */}
      <div className="flex items-center gap-2 px-4 pb-4">
        <motion.button
          onClick={handleDirectSend}
          disabled={isSending}
          whileHover={{ scale: isSending ? 1 : 1.02 }}
          whileTap={{ scale: isSending ? 1 : 0.98 }}
          className={cn(
            "flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium rounded-lg shadow-xs transition-all",
            isSending
              ? "bg-primary-400 text-white cursor-wait"
              : "bg-linear-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white hover:shadow-md"
          )}
        >
          {isSending ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>Wird gesendet...</span>
            </>
          ) : (
            <>
              <Send className="h-4 w-4" />
              <span>Direkt senden</span>
            </>
          )}
        </motion.button>

        <motion.button
          onClick={() => setIsExpanded(true)}
          disabled={isSending}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          className="flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium bg-background hover:bg-muted text-foreground rounded-lg border border-border shadow-xs hover:shadow-md transition-all"
        >
          <Edit2 className="h-4 w-4" />
          <span className="hidden sm:inline">Bearbeiten</span>
        </motion.button>
      </div>
    </motion.div>
  );
}
